import React, { useEffect, useRef, useState, useCallback } from 'react';

import { Platform, TextInput, View } from 'react-native';

import { debounce } from 'lodash';

import { useTheme } from 'styled-components/native';
import Icon from 'react-native-vector-icons/dist/Feather';
import { SelectItem } from '../@hello-ui/Select/types';
import { AlertIconOutlined } from '../Icons/AlertIconOutlined';
import { StatesTypes } from '~/components/Dialog/States/types';
import { CitiesTypes } from '~/components/Dialog/Cities/types';
import { NeighborhoodsTypes } from '~/components/Dialog/Neighborhoods/types';
import {
  Button,
  Typography,
  useAdresses,
  Input,
  Autocomplete,
  type AutocompleteType,
  Checkbox,
  MobileAutoSpace,
} from '~/components/@hello-ui';
import { localCapitalize } from '~/utils/strings';
import { useGeolocation } from '~/components/@hello-ui/Adresses/Geolocation';
import { GeolocationButton } from '~/components/@hello-ui/Adresses/Geolocation/component/GeolocationButton';
import { useApi } from '~/hooks/api';

export interface Location {
  state: StatesTypes;
  city: CitiesTypes;
  neighborhood: NeighborhoodsTypes[];
  postalCode: string;
  street: string;
}

interface ScheduleLocationProps {
  title: string;
  subtitle?: string;
  textButton?: string;
  onConfirmedLocation?: (location: Location) => void;
  buttonLoading?: boolean;
}

export type Place = {
  state: string;
  city: string;
  neighborhood: string;
  street: string;
  number: string;
  postalCode: string;
};

export const SelectLocal = ({
  title,
  subtitle,
  textButton = 'Próximo',
  onConfirmedLocation,
  buttonLoading,
}: ScheduleLocationProps): JSX.Element => {
  const [suggestions, setSuggestions] = useState<AutocompleteType<string>[]>([]);
  const [highlight, setHighlight] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(false);
  const [place, setPlace] = useState<Partial<Place> | undefined>(undefined);
  const [numberDisable, setNumberdisable] = useState<boolean>(false);
  const [nextIsDisable, setNextIsDisable] = useState<boolean>(true);

  const api = useApi();
  const theme = useTheme();

  const searchInputRef = useRef<TextInput>(null);
  // daqui pra baixo e tudo legado!

  const { getState, getCity, getNeighborhoods, getStates, getCities } = useAdresses();
  const { geolocationAddress } = useGeolocation();

  const [state, setState] = useState<StatesTypes | undefined>(undefined);
  const [city, setCity] = useState<CitiesTypes | undefined>(undefined);
  const [neighborhood, setNeighborhood] = useState<NeighborhoodsTypes[] | undefined>(undefined);

  const [states, setStates] = useState<any>();
  const [cities, setCities] = useState<any>();
  const [neighborhoods, setNeighborhoods] = useState<SelectItem[]>([]);
  const [address, setAddress] = useState<string>();
  const [numberPlaceholder, setNumberPlaceholder] = useState<string>('Digite o número do endereço');
  const isTypingRef = useRef<boolean>(false);

  const stateSelectRef = useRef<any>(null);
  const citySelectRef = useRef<any>(null);
  const neighborhoodSelectRef = useRef<any>(null);

  useEffect(() => {
    if (!isTypingRef.current) {
      fillFieldsWithGeolocationData();
    }
  }, [geolocationAddress]);

  useEffect(() => {
    getAndSetStates();
  }, []);

  const updateSelectedState = async (
    newState?: StatesTypes | undefined,
    bulkSet: boolean = false,
  ) => {
    setState(newState);

    if (newState) {
      await getAndSetCities(newState.id);
    }

    if (!bulkSet) {
      setCity(undefined);
      setNeighborhood(undefined);
      setNeighborhoods([]);

      citySelectRef.current?.reset?.();
      neighborhoodSelectRef.current?.reset?.();
    }
  };

  const updateSelectedCity = async (
    newCity?: CitiesTypes | undefined,
    bulkSet: boolean = false,
  ) => {
    setCity(newCity);

    if (newCity) {
      await getAndSetNeighborhoods(newCity.id);
    }

    if (!bulkSet) {
      setNeighborhood(undefined);
      setNeighborhoods([]);
      neighborhoodSelectRef.current?.reset?.();
    }
  };

  const getAndSetStates = async () => {
    try {
      const result = await getStates();
      setStates(
        result?.map((v) => ({
          ...v,
          title: localCapitalize(v.title),
          label: localCapitalize(v.title),
          value: v.id,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getAndSetCities = async (stateId: number) => {
    try {
      const result = await getCities(stateId);
      setCities(
        result.map((v: any) => ({
          ...v,
          title: localCapitalize(v?.title),
          label: localCapitalize(v?.title),
          value: v?.id,
        })),
      );
      return result;
    } catch (error) {
      console.error(error);
    }
  };

  const getAndSetNeighborhoods = async (cityId: number, value?: string) => {
    getNeighborhoods(cityId, value ?? '').then((data) =>
      setNeighborhoods(
        data.map(({ title, id }) => ({
          title: localCapitalize(title),
          label: localCapitalize(title),
          value: id,
        })),
      ),
    );
  };

  const fillFieldsWithGeolocationData = async () => {
    if (geolocationAddress.state) {
      searchInputRef?.current?.setNativeProps({ text: geolocationAddress.street });
      setAddress(geolocationAddress.street);
      setPlace({
        state: geolocationAddress.state,
        city: geolocationAddress.city,
        neighborhood: geolocationAddress.neighborhood,
        street: geolocationAddress.street,
        number: geolocationAddress?.number,
        postalCode: geolocationAddress.cep,
      });
    }
  };

  const handleConfirmLocation = async () => {
    if (onConfirmedLocation) {
      onConfirmedLocation({
        state,
        city,
        neighborhood,
      });
    }
  };

  const handleSearch = useCallback(
    debounce(async (text: string) => {
      isTypingRef.current = true;
      if (!text) {
        setVisible(false);
        setPlace(undefined);
        return;
      }

      setHighlight(text);
      await api.placeAutocomplete(text).then((res) => {
        setSuggestions(
          res.map(({ placeId, description }) => ({
            id: placeId,
            value: placeId,
            placeholder: description,
          })),
        );
        setVisible(true);
      });
      isTypingRef.current = false;
    }, 250),
    [],
  );

  const onSelect = async (suggestion: AutocompleteType<string>) => {
    const place = await api.placeById(suggestion.value);
    setPlace({
      street: place?.street,
      city: place.city,
      state: place.state,
      neighborhood: place.neighborhood,
      postalCode: place.postalCode,
    });

    searchInputRef.current?.setNativeProps({ text: suggestion.placeholder });
    setVisible(false);
  };

  const handleNextStep = () => {
    onConfirmedLocation(place);
  };

  const handleNumber = (value) => {
    setPlace((prev) => ({
      ...prev,
      number: value,
    }));
  };

  const handleNeighborhood = (value) => {
    setPlace((prev) => ({
      ...prev,
      neighborhood: value,
    }));
  };

  useEffect(() => {
    if (numberDisable) {
      if (place?.city && place?.state && place?.neighborhood) {
        setNextIsDisable(false);
      } else {
        setNextIsDisable(true);
      }
      setPlace((prev) => ({
        ...prev,
        number: '',
      }));
      setNumberPlaceholder('Sem número');
    } else {
      if (
        place?.number &&
        place?.number !== '' &&
        place?.city &&
        place?.state &&
        place?.neighborhood
      ) {
        setNextIsDisable(false);
      } else {
        setNumberPlaceholder('Digite o número do endereço');
        setNextIsDisable(true);
      }
    }
  }, [numberDisable, place?.number, place?.neighborhood]);

  return (
    <View className="mb-24 h-full w-full max-w-[530px]">
      <View className={subtitle ? 'mb-24 mobile:mb-16' : 'mb-40 mobile:mb-16'}>
        <Typography variant="title">{title}</Typography>
        {subtitle && (
          <Typography className="mt-8 mobile:mt-4" variant="body2">
            {subtitle}
          </Typography>
        )}
      </View>

      <View className="mb-24 w-full flex-row mobile:mb-16">
        <GeolocationButton />
      </View>

      <View style={{ marginBottom: theme.isMobile ? 9 : 24 }}>
        <Autocomplete
          data={suggestions}
          onSelect={onSelect}
          highlight={highlight}
          visible={visible}>
          <Input
            variant="search"
            disableErrorHandle
            ref={searchInputRef}
            onChangeText={handleSearch}
            label="Endereço ou CEP"
            placeholder="Digite o endereço ou CEP"
          />
        </Autocomplete>
      </View>

      <View className="flex-1 flex-col ">
        {place && (
          <View className="flex-row flex-wrap" style={{ marginBottom: theme.isMobile ? -6 : -4 }}>
            <View
              className="flex w-full flex-row space-x-4 "
              style={{ marginBottom: theme.isMobile ? -9 : -4 }}>
              <View className="w-1/2 pr-8">
                <Input variant="simple" label="Cidade" value={place.city} isDisabled />
              </View>
              <View className="w-1/2 pr-8">
                <Input variant="simple" label="Estado" value={place.state} isDisabled />
              </View>
            </View>

            <View style={{ flexDirection: 'column' }} className="flex-column w-full">
              <View
                style={{ marginBottom: theme.isMobile ? -9 : -4, flexDirection: 'row' }}
                className="flex-column w-full">
                <Input
                  variant="simple"
                  label="Bairro"
                  onChangeText={handleNeighborhood}
                  value={place.neighborhood}
                  placeholder="Digite o nome do seu bairro"
                />
              </View>
              <View
                style={{ marginBottom: theme.isMobile ? -9 : -4, flexDirection: 'row' }}
                className="flex-column w-full">
                <Input
                  variant="simple"
                  label="Número"
                  onChangeText={handleNumber}
                  value={place.number}
                  placeholder={numberPlaceholder}
                  isDisabled={numberDisable}
                  maxLength={6}
                />
              </View>
              <View className="flex flex-row space-x-2">
                <Checkbox
                  disabled={false}
                  bgColor={theme?.colors.white}
                  color={theme?.colors.primary}
                  border={theme?.colors.primary}
                  borderWidth={2}
                  iconColor={theme?.colors.primary}
                  onPress={() => setNumberdisable(!numberDisable)}
                />
                <Typography variant="body1" className="text-sm">
                  Endereço sem número
                </Typography>
              </View>
            </View>
          </View>
        )}
        {/* {theme.isMobile && 
              <View style={{ backgroundColor: "#FFF4DF" }} className="mb-24 mt-3 rounded-lg p-16 mobile:mb-16">
                <View className="flex flex-row space-x-4">
                  <AlertIconOutlined width={25} height={25}/>
                  <Typography variant="bodyHighlight2" color='#FA9F47'>Resultados podem variar</Typography>
                </View>
                
               
               <Typography variant="body2">
                 <Typography variant="bodyHighlight2">A busca por prestadores</Typography> usa <Typography variant="bodyHighlight2">um raio de localização. </Typography>
                 Isso significa que, mesmo informando um <Typography variant="bodyHighlight2">CEP ou bairro, </Typography> os resultados podem
                 <Typography variant="bodyHighlight2"> não incluir médicos exatamente nessa região.</Typography>
               </Typography>
    
            </View>
        }  */}
        <Button
          variant="primary"
          style={{
            marginLeft: 0,
            marginBottom: Platform.OS === 'ios' ? 10 : Platform.OS === 'android' ? 15 : 0,
          }}
          className="mt-auto mobile:mt-auto"
          icon={<Icon name="chevron-right" size={18} color={theme.colors.white} />}
          iconPosition="right"
          disabled={nextIsDisable}
          onPress={handleNextStep}>
          Próximo
        </Button>
      </View>
    </View>
  );
};
